import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u3_x2_3punkt';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: 'Einleitung',
    body: '3-Punkt-Stellantriebe sind eine verbreitete Lösung in der Gebäudeautomation, um Ventile und Klappen nicht nur zu öffnen und zu schließen, sondern auch in Zwischenpositionen zu halten. Sie bieten eine modulierende Regelung und werden häufig für Mischer oder Luftklappen eingesetzt.'
  },
  {
    heading: 'Was bedeutet "3-Punkt-Antrieb"?',
    body: 'Ein 3-Punkt-Antrieb kann drei Zustände einnehmen:\n\n1. Antrieb fährt AUF (Drehrichtung 1)\n2. Antrieb fährt ZU (Drehrichtung 2)\n3. Antrieb HÄLT Position (kein Steuersignal aktiv)\n\nDer Name leitet sich von den typischerweise drei elektrischen Anschlusspunkten für die Steuerung ab: ein gemeinsamer Leiter und je ein Steuerkontakt für die AUF- und ZU-Bewegung. Solange an keinem der beiden Steuerkontakte Spannung anliegt, verharrt der motorische Antrieb in seiner aktuellen Stellung.'
  },
  {
    heading: 'Funktionsweise und Ansteuerung',
    body: '3-Punkt-Antriebe sind immer motorische Antriebe. Ein Regler (z.B. Heizungsregler, DDC) gibt kurze Spannungsimpulse:\n\n•   Signal an Eingang Y1 (AUF): Motor dreht in eine Richtung, Ventil öffnet.\n•   Signal an Eingang Y2 (ZU): Motor dreht in entgegengesetzte Richtung, Ventil schließt.\n•   Kein Signal an Y1 & Y2: Motor stoppt, Ventil hält die aktuelle Position.\n\nDer Regler tastet sich so an die gewünschte Ventilposition heran, um z.B. eine Vorlauftemperatur konstant zu halten.\n\nWichtig: Es darf niemals gleichzeitig Spannung an beiden Steuereingängen (Y1 und Y2) anliegen! Dies muss der Regler sicherstellen.'
  },
  {
    heading: 'Typischer Elektrischer Anschluss',
    body: 'Ein Standard-3-Punkt-Antrieb (z.B. für 230V AC) hat meist folgende Klemmen:\n\n•   N (oder G0, System Neutral): Neutralleiter, gemeinsamer Bezugspunkt.\n•   Y1 (oder BK, Pfeil AUF, Klemme 1): Steuerphase für Drehrichtung 1 (z.B. Öffnen).\n•   Y2 (oder RD, Pfeil ZU, Klemme 2): Steuerphase für Drehrichtung 2 (z.B. Schließen).\n\nBei 24V Antrieben ist N oft als G0 (Systempotential Minus) und Y1/Y2 als G (Systempotential Plus geschaltet) ausgeführt.'
  },
  {
    heading: 'Vor-/Nachteile und Einsatzgebiete',
    body: 'Vorteile:\n•   Ermöglicht modulierende Regelung (quasi-stetig).\n•   Genauer als einfache 2-Punkt-Regelung.\n•   Robuste und bewährte Technik.\n•   Kann höhere Stellkräfte als thermische Antriebe erzeugen.\n\nNachteile:\n•   Komplexere Ansteuerung (Regler mit 3-Punkt-Ausgang nötig).\n•   Keine automatische Rückstellung bei Stromausfall.\n•   Kann Laufgeräusche verursachen.\n\nEinsatzgebiete:\n•   Regelung von Mischern (Vorlauftemperaturregelung in Heizkreisen)\n•   Regelventile in HLK-Anwendungen\n•   Industrielle Prozesse'
  }
];

const images = [
  { id: 'img_va3p_sauter', label: '3-Punkt Sauter Antrieb', src: `${IMAGE_BASE}ventil_3p_sauterantrieb.webp`, alt: 'Beispiel 3-Punkt Ventilantrieb' },
  { id: 'img_va3p_funktion', label: 'Funktionsprinzip', src: `${IMAGE_BASE}ventil_3p_funktion.webp`, alt: '3-Punkt Ansteuerung Prinzip' },
  { id: 'img_va3p_schaltplan', label: 'Schaltplan', src: `${IMAGE_BASE}schaltplan_3punkt.PNG`, alt: 'Schaltplan 3-Punkt-Antrieb' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.3.2 3-Punkt-Ventilantriebe', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u3_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


